import { createContext, useContext, ReactNode, useCallback } from 'react';

import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';

import accountsClient, { AccountDetails } from '@clients/accountsClient';
import { Program } from '@clients/domains/programs/program';
import { ProgramParameter } from '@clients/domains/programs/programParameters';
import programsClient from '@clients/programsClient';
import useGetProgramParameters from '@hooks/queries/useGetProgramParameters';
import notifyAPIError from '@utils/notifyAPIError';

export type AccountContextError = {
    accountError: Error;
    accountIsError: boolean;
    programError: Error;
    programIsError: boolean;
};

export interface AccountRefetch {
    accountRefetch: () => void;
}

export interface AccountContextType {
    isLoading: boolean;
    account: AccountDetails;
    program: Program;
    programParameters: ProgramParameter[];
    error: AccountContextError;
    refetch?: AccountRefetch;
    programHasParameter: (parameterName: string) => boolean;
}

const AccountContext = createContext<AccountContextType>(null);

type AccountProviderProps = {
    children: ReactNode;
};

const { getAccountDetails } = accountsClient();
const { getProgramDetails } = programsClient();

const AccountProvider = ({ children }: AccountProviderProps) => {
    const { accountId } = useParams();

    const {
        data: accountData,
        error: accountError,
        isError: accountIsError,
        isLoading: accountLoading,
        refetch: accountRefetch,
    } = useQuery(
        ['accountDetails', accountId],
        () => getAccountDetails({ accountId: parseFloat(accountId) }),
        {
            onError: notifyAPIError,
        },
    );

    const programId = accountData?.program_id;

    const {
        data: programData,
        error: programError,
        isError: programIsError,
        isLoading: programLoading,
    } = useQuery(
        ['programDetails', programId],
        () => getProgramDetails({ programId }),
        {
            // The query will not execute until the programId exists from the query above
            enabled: !!programId,
        },
    );

    const {
        data: programParametersData,
        isLoading: isLoadingProgramParameters,
        isError: programParametersIsError,
        error: programParametersError,
    } = useGetProgramParameters({ programId });

    const isLoading =
        accountLoading || programLoading || isLoadingProgramParameters;

    const error = {
        accountError: accountError as Error,
        accountIsError,
        programError: programError as Error,
        programIsError,
        programParametersError: programParametersError as Error,
        programParametersIsError,
    };

    const refetch = {
        accountRefetch,
    };

    const programHasParameter = useCallback(
        (parameterName: string) => {
            return !!programParametersData?.items?.some(
                (parameter) =>
                    parameter.name === parameterName && parameter.value !== 0,
            );
        },
        [programParametersData],
    );

    return (
        <AccountContext.Provider
            value={{
                error,
                isLoading,
                account: accountData,
                program: programData,
                programParameters: programParametersData?.items,
                refetch,
                programHasParameter,
            }}>
            {children}
        </AccountContext.Provider>
    );
};

const useAccount = () => useContext(AccountContext);

export { AccountProvider, useAccount };
export default AccountContext;
